import { trpc } from './trpc'
import { inferRouterOutputs, inferRouterInputs } from '@trpc/server'
import { AppRouter } from '@tagadapay/trpc-types'
import { useToast } from '@/components/ui/use-toast'

type OutputRouter = inferRouterOutputs<AppRouter>
type InputRouter = inferRouterInputs<AppRouter>

// Output types
export type PricesListOutput = OutputRouter['prices']['list']
export type PriceItem = PricesListOutput['prices'][number]
export type PriceDetails = OutputRouter['prices']['getById']
export type UpdatePriceOutput = OutputRouter['prices']['update']
export type GetPricesOutput = OutputRouter['prices']['getPrices']

// Input types
export type ListPricesInput = InputRouter['prices']['list']
export type GetPriceInput = InputRouter['prices']['getById']
export type UpdatePriceInput = InputRouter['prices']['update']
export type GetPricesInput = InputRouter['prices']['getPrices']

// Query hooks
export const usePricesList = (params: ListPricesInput, enabled = true) => {
    return trpc.prices.list.useQuery(params, {
        enabled: enabled && !!params.productId,
    })
}

export const usePrice = (priceId: string, enabled = true) => {
    return trpc.prices.getById.useQuery(
        { priceId },
        {
            enabled: enabled && !!priceId,
            retry: 2,
            staleTime: 30000,
        },
    )
}

export const usePrices = (params: GetPricesInput, enabled = true) => {
    return trpc.prices.getPrices.useQuery(params, {
        enabled: enabled && !!params.productId,
    })
}

// Mutation hooks
export const useUpdatePrice = () => {
    const { toast } = useToast()
    const utils = trpc.useUtils()

    return trpc.prices.update.useMutation({
        onSuccess: () => {
            utils.prices.list.invalidate()
            utils.prices.getById.invalidate()
            utils.prices.getPrices.invalidate()
            toast({
                title: 'Success',
                description: 'Price updated successfully',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to update price',
                variant: 'destructive',
            })
        },
    })
}

